import React, { type FC, memo } from 'react';
import { Box, Button, Checkbox, CheckboxGroup, ModalBase } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import { Creative } from 'packages/store/creatives/types';
import DesignsList from './DesignsList';
import Preset from './Preset';
import Formats from './Formats';
import generateTestId from '~/helpers/testIdHelpers';
import { Alert } from 'packages/pages/designsBulkExportModal/Alert';
import useDesignsBulkExportModal from './useDesignsBulkExportModal';
import modalContainer from 'packages/common/modalContainer';
import useAccessRights from 'packages/hooks/useAccessRights';
import features from '~/configs/features';

type DesignsBulkExportModalProps = {
    isOpen: boolean;
    designs: Creative[];
    onClose: () => void;
};

const DesignsBulkExportModal: FC<DesignsBulkExportModalProps> = ({ isOpen, designs, onClose }) => {
    const { translate } = useTranslate();
    const { isPlatformAllowed } = useAccessRights();
    const {
        handleExport,
        isExporting,
        isLoadingRenditions,
        isDownloadBlocked,
        isWaitingRoomBlocked,
        isTranscribeEnabled,
        exportableDesignsLength,
        incompatibleDesignsCount,
        exportResults,
        creativeTypes,
        alertMessage,
        selectedProfiles,
        handleSelectPreset,
        handleRenditionsLoad,
        handleSelectProfile,
        preferredConfigurationIdsByType,
        handleTranscribeChange,
    } = useDesignsBulkExportModal(designs, isOpen, onClose);

    const isTranscribable = isPlatformAllowed([features.TRANSCRIPT]) && selectedProfiles?.VIDEO;

    return (
        <ModalBase
            container={modalContainer}
            isOpen={isOpen}
            onClose={onClose}
            {...generateTestId('designs-bulk-export-modal')}
        >
            <ModalBase.Header title={translate('modal.bulk-export.title')} />
            <ModalBase.Content>
                {!!alertMessage && <Alert>{alertMessage}</Alert>}
                {incompatibleDesignsCount > 0 && (
                    <Alert variant="warning">
                        {translate('modal.bulk-export.unsupported-profile-warning', {
                            count: incompatibleDesignsCount,
                        })}
                    </Alert>
                )}
                <DesignsList designs={designs} exportResults={exportResults} selectedProfiles={selectedProfiles} />
                <Box paddingTop="5" paddingBottom="5">
                    <Preset
                        isLoadingRenditions={isLoadingRenditions}
                        isDownloadBlocked={isDownloadBlocked}
                        isWaitingRoomBlocked={isWaitingRoomBlocked}
                        isExporting={isExporting}
                        onSelect={handleSelectPreset}
                    />
                    <Formats
                        creativeTypes={creativeTypes}
                        onLoadRenditions={handleRenditionsLoad}
                        onSelectProfile={handleSelectProfile}
                        preferredConfigurationIdsByType={preferredConfigurationIdsByType}
                    />
                </Box>
                {isTranscribable && (
                    <CheckboxGroup label={translate('modal.bulk-export.transcribe.title')}>
                        <Checkbox onChange={handleTranscribeChange} isChecked={isTranscribeEnabled}>
                            {translate('modal.bulk-export.transcribe.label')}
                        </Checkbox>
                    </CheckboxGroup>
                )}
            </ModalBase.Content>
            <ModalBase.Footer
                actionPrimary={
                    <Button
                        variant="primary"
                        onClick={handleExport}
                        isLoading={isExporting}
                        isDisabled={
                            exportableDesignsLength === 0 ||
                            isLoadingRenditions ||
                            (isDownloadBlocked && isWaitingRoomBlocked)
                        }
                    >
                        {translate('modal.bulk-export.export', { count: exportableDesignsLength })}
                    </Button>
                }
                actionSecondary={
                    <Button variant="secondary" onClick={onClose}>
                        {translate('modal.bulk-export.cancel')}
                    </Button>
                }
            />
        </ModalBase>
    );
};

export default memo(DesignsBulkExportModal);
